import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionDropdown,
  ActionDropdownAction,
  InputSearch,
  Pagination,
  Status,
  TableData,
} from '@/components';
import { Column } from '@/types/common';
import {
  DriverOutstanding,
  DriverOutstandingStatus,
} from '@/types/outstanding';
import { getOutstandings } from '@/services/api/outstandings';
import { showErrorToast } from '@/components/toast/globalToast';
import { extractApiErrorMessage, formatRupiah } from '@/utils';
import {
  availableActions,
  driverLabel,
  driverSubLabel,
  formatDate,
  outstandingSearchHaystack,
  sourceTypeLabel,
} from './outstandingDisplay';
import { useOutstandingActions } from './useOutstandingActions';
import OutstandingActionModals from './components/OutstandingActionModals';

type ListTab = 'all' | DriverOutstandingStatus;

const TABS: Array<{ key: ListTab; label: string }> = [
  { key: 'all', label: 'Semua' },
  { key: DriverOutstandingStatus.OPEN, label: 'Open' },
  {
    key: DriverOutstandingStatus.PARTIALLY_SETTLED,
    label: 'Partially Settled',
  },
  { key: DriverOutstandingStatus.SETTLED, label: 'Settled' },
  { key: DriverOutstandingStatus.RELEASED, label: 'Released' },
];

const OutstandingListPage: React.FC = () => {
  const navigate = useNavigate();
  const [allRows, setAllRows] = useState<DriverOutstanding[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ListTab>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<string | null>(
    null
  );
  const {
    modal,
    openModal,
    closeModal,
    isSubmitting,
    handleSettle,
    handleRelease,
    handleOverride,
  } = useOutstandingActions((updated) =>
    setAllRows((rows) =>
      rows.map((row) => (row.id === updated.id ? updated : row))
    )
  );

  useEffect(() => {
    // No filters sent — the whole set is fetched once so the tab counts and
    // every tab's rows come from a single request. Search runs client-side
    // over that set (mitra name/code), which the old server-side driverID
    // filter could not do.
    const load = async () => {
      setIsLoading(true);
      try {
        setAllRows(await getOutstandings());
      } catch (error) {
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to load outstandings')
        );
        setAllRows([]);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const handleOutsideClick = () => setActionDropdownOpen(null);
    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleOutsideClick);
    }
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [actionDropdownOpen]);

  const searchedRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allRows;
    return allRows.filter((row) =>
      outstandingSearchHaystack(row).includes(term)
    );
  }, [allRows, searchTerm]);

  const tabCounts = useMemo(() => {
    const counts: Record<ListTab, number> = {
      all: searchedRows.length,
      [DriverOutstandingStatus.OPEN]: 0,
      [DriverOutstandingStatus.PARTIALLY_SETTLED]: 0,
      [DriverOutstandingStatus.SETTLED]: 0,
      [DriverOutstandingStatus.RELEASED]: 0,
    };
    searchedRows.forEach((row) => {
      counts[row.status] = (counts[row.status] ?? 0) + 1;
    });
    return counts;
  }, [searchedRows]);

  const tabRows = useMemo(
    () =>
      activeTab === 'all'
        ? searchedRows
        : searchedRows.filter((row) => row.status === activeTab),
    [searchedRows, activeTab]
  );

  const pageRows = useMemo(
    () =>
      tabRows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [tabRows, currentPage, pageSize]
  );

  const handleTabChange = (tab: ListTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleToggleDropdown = useCallback((id: string) => {
    setActionDropdownOpen((prev) => (prev === id ? null : id));
  }, []);

  const columns: Column<DriverOutstanding>[] = [
    {
      key: 'driver',
      title: 'Mitra Name',
      render: (_value, record) => (
        <div className="leading-tight">
          <div className="text-[14px] text-gray-900">
            {driverLabel(record)}
          </div>
          <div className="text-[12px] text-gray-500">
            {driverSubLabel(record)}
          </div>
        </div>
      ),
    },
    {
      key: 'sourceType',
      title: 'Sumber',
      render: (_value, record) => sourceTypeLabel(record),
    },
    {
      key: 'amountOriginal',
      title: 'Amount Original',
      align: 'right',
      render: (_value, record) =>
        formatRupiah(record.amountOriginal, '-', { space: false }),
    },
    {
      key: 'amountOutstanding',
      title: 'Sisa Outstanding',
      align: 'right',
      render: (_value, record) => (
        <span className="font-medium text-gray-900">
          {formatRupiah(record.amountOutstanding, '-', { space: false })}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_value, record) => (
        <div className="flex items-center gap-1.5">
          <Status
            value={record.status}
            type="driverOutstandingStatus"
            size="small"
          />
          {record.newRentalOverrideBy && (
            <span
              title={record.newRentalOverrideReason ?? undefined}
              className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-blue-600"
            >
              Override aktif
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      title: 'Dibuat',
      render: (_value, record) => formatDate(record.createdAt),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-16',
      align: 'center',
      render: (_value, record) => {
        const { canSettle, canRelease, canOverride } =
          availableActions(record);
        const actions: ActionDropdownAction[] = [
          {
            key: 'detail',
            label: 'Lihat Detail',
            icon: 'ri-eye-line',
            onClick: () => {
              setActionDropdownOpen(null);
              navigate(`/outstandings/${record.id}`);
            },
          },
        ];
        if (canSettle) {
          actions.push({
            key: 'settle',
            label: 'Settle',
            icon: 'ri-hand-coin-line',
            onClick: () => {
              setActionDropdownOpen(null);
              openModal({ type: 'settle', row: record });
            },
          });
        }
        if (canOverride) {
          actions.push({
            key: 'override',
            label: 'Allow New Rental',
            icon: 'ri-shield-check-line',
            onClick: () => {
              setActionDropdownOpen(null);
              openModal({ type: 'override', row: record });
            },
          });
        }
        if (canRelease) {
          actions.push({
            key: 'release',
            label: 'Release (Write Off)',
            icon: 'ri-close-circle-line',
            className: 'text-red-600',
            onClick: () => {
              setActionDropdownOpen(null);
              openModal({ type: 'release', row: record });
            },
          });
        }
        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === record.id}
            onToggle={() => handleToggleDropdown(record.id)}
            actions={actions}
            ariaLabel={`Actions for outstanding ${record.id}`}
          />
        );
      },
    },
  ];

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
          Outstanding
        </h1>
        <p className="text-[14px] text-gray-600">
          Sisa tagihan driver dari kerusakan yang melebihi deposit — pantau,
          settle, atau release di sini.
        </p>
      </div>

      <div className="border-b border-gray-200 mb-4">
        <nav className="flex gap-6" aria-label="Outstanding sections">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabChange(tab.key)}
                aria-current={isActive ? 'page' : undefined}
                className={`-mb-px flex items-center gap-2 border-b-2 px-1 pb-3 text-[15px] transition-colors ${
                  isActive
                    ? 'border-primary font-medium text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    isActive
                      ? 'bg-purple-100 text-primary'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {tabCounts[tab.key]}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-end gap-2">
          <InputSearch
            placeholder="Cari nama atau kode mitra..."
            value={searchTerm}
            // Filtering is an array pass over rows already in memory, so it
            // runs on every keystroke — nothing to debounce.
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            size="small"
          />
        </div>
      </div>

      <div className="relative">
        {isLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-gray-600">Loading outstanding...</span>
          </div>
        ) : (
          <TableData
            data={pageRows}
            columns={columns}
            size="small"
            className="min-w-full"
            showHeaderWhenEmpty
            onRowClick={(record) => navigate(`/outstandings/${record.id}`)}
            emptyState={
              <div className="py-20">
                <div className="w-[148px] h-[148px] mx-auto mb-4">
                  <img
                    src="/images/no-results.png"
                    alt="No outstanding yet"
                    className="w-full h-full"
                  />
                </div>
                <h3
                  className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] text-gray-900 mb-2 text-center"
                  style={{ letterSpacing: '-1.5%' }}
                >
                  Tidak Ada Outstanding
                </h3>
                <p
                  className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] leading-[20px] text-gray-500 text-center max-w-[400px] mx-auto"
                  style={{ letterSpacing: '-0.6%' }}
                >
                  Sisa tagihan driver dari kerusakan yang melebihi deposit
                  akan muncul di sini.
                </p>
              </div>
            }
          />
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={tabRows.length}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setCurrentPage(1);
        }}
      />

      <OutstandingActionModals
        modal={modal}
        onClose={closeModal}
        isSubmitting={isSubmitting}
        onSettle={handleSettle}
        onRelease={handleRelease}
        onOverride={handleOverride}
      />
    </div>
  );
};

export default OutstandingListPage;
